import React from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router-dom';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import FormViewButton from 'components/Common/Buttons/FormViewButton';
import FormWorkflowStages from 'common/Workflows/FormWorkflowStages';

const CustomerFormList = ({ permissions, customer, onDeleteForm }) => {
  const getAccountName = (item) => {
    if (item === null || (!item.accountNumber && !item.accountName)) {
      return 'N/A';
    }

    let accountName = item.accountNumber;

    if (!accountName) {
      accountName = item.accountName;
    } else if (item.accountName !== null && item.accountName !== accountName) {
      accountName = `${ accountName } - ${ item.accountName }`;
    }

    return accountName;
  };

  const getGroupedFormsList = ({ forms }) => {
    const sortedList = forms?.sort((a, b) => {
      if (moment(b.validUntil).isSame(a.validUntil)) {
        return moment(b.dateUploaded).isAfter(a.dateUploaded) ? 1 : -1;
      }
      return moment(b.validUntil).isAfter(a.validUntil) ? 1 : -1;
    });

    const getAccount = ({ accountId, accountNumber, accountName, isValid, validUntil, expiryReasonDescription }) => ({ accountId, accountName, accountNumber, isValid, validUntil, expiryReasonDescription });

    const grouped = sortedList?.reduce((formCollection, currentForm) => {
      const key = `f${ currentForm.id }`;
      if (!formCollection.keys[key]) {
        formCollection.keys[key] = formCollection.values.length + 1;
        formCollection.values.push(currentForm);
        currentForm.accountList = [];
        currentForm.key = key;
      }

      const idx = formCollection.keys[key] - 1;
      formCollection.values[idx].accountList.push(getAccount(currentForm));

      return formCollection;
    }, { keys: {}, values: [] });

    return grouped?.values.sort((a, b) => (a.accountName > b.accountName ? -1 : 1));
  };

  const formExpiryContent = formAccount => <div className="inner-list-item">{(() => {
    if (!formAccount.isValid) {
      return 'N/A - Invalid Form';
    }
    if (formAccount.validUntil && formAccount.validUntil !== null) {
      const expiryReason = formAccount.expiryReasonDescription !== null ? `(${ formAccount.expiryReasonDescription })` : '';
      if (formAccount.validUntil.includes('9999')) {
        return `Evergreen ${ expiryReason }`;
      }
      return `${ moment(formAccount.validUntil).format('MMM DD, YYYY') } ${ expiryReason }`;
    }
    return '';
  })()}
  </div>;

  const formAccountContent = formAccount => <div className="inner-list-item">{
    (AccessControl.hasPermission(permissions, [ActivityPerms.accountPageView, ActivityPerms.accountEdit])
      ? <Link className="text-bold" to={ `/account/${ formAccount.accountId }` } sourcedata={ formAccount }> { getAccountName(formAccount) } </Link>
      : getAccountName(formAccount))
    }</div>;

  return (
    <div>
      <table className="table table-striped-groups table-hover table-bordered forms-list" id="customer-forms-table">
        <thead>
          <tr>
            <th>Form Type</th>
            <th>Date Uploaded</th>
            <th>Account</th>
            <th width="300px">Expiration Date</th>
            <th>Signed Date</th>
            <th>Workflow</th>
            <th>Original Filename</th>
            <th>View/Action</th>
          </tr>
        </thead>
        { (customer != null && customer.forms != null && customer.forms.length > 0)
          && getGroupedFormsList(customer).map(formData => formData != null && <tbody>
            <tr key={ formData.key } className="br-1 form-row">
              <td align="center" rowSpan={ formData.accountList.length }>{ formData.formTypeDescription }</td>
              <td align="center" rowSpan={ formData.accountList.length }>{ moment(formData.dateUploaded).format('MMM DD, YYYY') }</td>
              <td align="center" className="separated-list">
                { formData != null && formData.accountList != null && formData.accountList.length > 0 && formAccountContent(formData.accountList[0]) }
                { formData == null && formData.accountId == null && 'N/A' }
              </td>
              <td align="center" className="separated-list">
                { formData != null && formData.accountList != null && formData.accountList.length > 0
                  && formData.accountList.map((formAccount, index) => index === 0 && formExpiryContent(formAccount))}
              </td>
              <td align="center" rowSpan={ formData.accountList.length }> {formData.dateSigned !== null ? moment(formData.dateSigned).format('MMM DD, YYYY') : ''} </td>
              <td align="center" rowSpan={ formData.accountList.length }>{ formData.workflowStageDescription ?? 'N/A' }</td>
              <td rowSpan={ formData.accountList.length }>{ formData.originalFileName }</td>
              <td rowSpan={ formData.accountList.length }><div className="form-action-btn-group">
                {
                  (
                    (formData.isOnlineForm
                      && AccessControl.hasPermission(
                        permissions,
                        [
                          ActivityPerms.electronicView,
                          ActivityPerms.electronicValidate
                        ]
                      )
                    ) || (
                      !formData.isOnlineForm
                      && AccessControl.hasPermission(
                        permissions,
                        [
                          ActivityPerms.scannedView,
                          ActivityPerms.scannedEdit,
                          ActivityPerms.scannedValidate
                        ]
                      )
                    )
                  ) && formData.formType && formData.formType.toLowerCase() !== 'unknown' && <FormViewButton
                    content={ <span className="icon icon-apps" /> }
                    className="btn btn-sm"
                    title="Reopen"
                    formId={ formData.id }
                    accountId={ (!formData.accountList || formData.accountList.length !== 1) ? null : formData.accountId }
                    form={ formData }
                    permissions={ permissions }
                  />
                }
                {
                  (
                    ((
                      ((formData.isOnlineForm && AccessControl.hasPermission(permissions, [ActivityPerms.electronicDeleteCompleteForms]))
                      || (!formData.isOnlineForm && AccessControl.hasPermission(permissions, [ActivityPerms.scannedDeleteCompleteForms])))
                      && formData.workflowStage === FormWorkflowStages.Complete)
                    || (
                      ((formData.isOnlineForm && AccessControl.hasPermission(permissions, [ActivityPerms.electronicDeleteInReviewForms]))
                      || (!formData.isOnlineForm && AccessControl.hasPermission(permissions, [ActivityPerms.scannedDeleteInReviewForms])))
                      && formData.workflowStage !== FormWorkflowStages.Complete))
                    && formData.formType && formData.formType.toLowerCase() !== 'unknown'
                  )
                  && <button
                    className="btn btn-sm btn-danger"
                    type="button"
                    title="Delete form"
                    onClick={ () => onDeleteForm(formData) }
                  >
                    <span className="icon icon-delete" />
                  </button>
                }
              </div></td>
            </tr>
            { formData.accountList && formData.accountList.length > 1 && formData.accountList.map((formAccount, index) => index > 0 && <tr className="extra-data-row br-1">
              <td align="center" className="separated-list">{ formAccountContent(formAccount) }</td>
              <td align="center" className="separated-list">{ formExpiryContent(formData) }</td>
            </tr>)}
          </tbody>) }
        { (!customer || !customer.forms || customer.forms.length === 0)
          && <tbody><tr><td colSpan="8" style={ { textAlign: 'center' } }><p><b>No Data to Show</b></p></td></tr></tbody> }
      </table>
    </div>
  );
};

CustomerFormList.propTypes = {
  permissions: PropTypes.arrayOf(PropTypes.string),
  customer: PropTypes.shape(),
  onDeleteForm: PropTypes.func.isRequired
};

CustomerFormList.defaultProps = {
  permissions: null,
  customer: {
    forms: []
  }
};

export default CustomerFormList;
